import React, { useState, useEffect, useRef } from 'react';

/*
  ChatWidget — persistent launcher card + expandable chat panel (bottom-right).
  Collapsed state: card with live green dot + "Chat with Live Agent" + button.
  Expanded state: full chat panel with message history.
  Connects to /api/finance?action=unified-chat.

  Resilience layer: if the backend errors (LLM down, missing API key, network
  failure, auth/DB hiccup) the widget never shows a raw error. It first tries to
  match the question against COMMON_RESPONSES (keyword FAQ), and otherwise falls
  back to a friendly generic message so the conversation always stays useful.
*/

// Keyword-matched canned answers for the most common questions. Used as an
// offline fallback whenever the live backend can't be reached.
const COMMON_RESPONSES = [
  {
    keywords: ['80c', 'tax saving', 'save tax', 'deduction', 'ppf', 'elss'],
    reply: 'Section 80C lets you claim up to Rs.1,50,000 in deductions under the old regime — via ELSS, PPF, EPF, life insurance premiums, and principal on a home loan. The new regime skips 80C but offers lower slab rates. Sign in and upload your data and I can tell you which regime saves you more. For your specific situation, consult a CA.',
  },
  {
    keywords: ['new regime', 'old regime', 'which regime', 'tax regime'],
    reply: 'The new regime has lower slab rates but removes most deductions (80C, HRA, etc.); the old regime keeps them. As a rule of thumb, if your deductions exceed ~Rs.3,75,000 the old regime usually wins. Upload your salary details and I\'ll compute the exact break-even for you.',
  },
  {
    keywords: ['advance tax', 'quarterly tax'],
    reply: 'Advance tax is due if your total tax liability exceeds Rs.10,000 in a year, paid in four installments (15% by Jun 15, 45% by Sep 15, 75% by Dec 15, 100% by Mar 15). Miss it and interest accrues under 234B/234C. I can estimate your installments once your income data is in.',
  },
  {
    keywords: ['salary', 'underpaid', 'market rate', 'raise', 'hike', 'negotiate'],
    reply: 'To benchmark your salary I compare your role, level, and location against current market data. Upload your resume or add your role details and I\'ll tell you whether you\'re at, above, or below market — and by how much.',
  },
  {
    keywords: ['savings rate', 'how much save', 'saving enough', 'budget'],
    reply: 'A healthy savings rate is 20%+ of your income; 30%+ is excellent. Once you connect your income and expenses I\'ll calculate your exact rate and flag where it\'s leaking.',
  },
  {
    keywords: ['upload', 'document', 'bank statement', 'how to start', 'get started'],
    reply: 'Head to your dashboard and upload a bank statement, salary slip, or resume. I read them automatically and use the real numbers to answer your money, tax, and career questions — no manual entry needed.',
  },
  {
    keywords: ['legal', 'contract', 'notice', 'agreement', 'rent', 'lawyer'],
    reply: 'I can give educational guidance on common legal matters — rental agreements, employment terms, notices, and consumer issues. Tell me what you\'re dealing with. For actual legal action, consult a licensed advocate.',
  },
  {
    keywords: ['what can you do', 'help', 'who are you', 'what is axigon'],
    reply: "I'm Axigon, a unified AI advisor covering your money, career, and legal questions in one place. Ask me anything across those three areas. Sign up and upload a document and I'll tailor answers to your actual numbers.",
  },
];

// Friendly generic messages shown when a request fails and no common response
// matches. Rotated so repeated failures don't feel robotic.
const GENERIC_FALLBACKS = [
  'I\'m having trouble reaching the live service right now. Please try again in a moment — or ask me about tax savings, salary benchmarking, or budgeting and I can still help.',
  'Sorry, I couldn\'t process that just now. Give it another try shortly. In the meantime, feel free to ask about 80C deductions, the new vs old tax regime, or how to get started.',
  'Something went wrong on my end — not on yours. Please retry in a few seconds. You can also ask me common questions about money, career, or legal matters while I recover.',
];

// Find a canned answer whose keywords appear in the user's message.
const matchCommonResponse = (text) => {
  const q = text.toLowerCase();
  const hit = COMMON_RESPONSES.find(c => c.keywords.some(k => q.includes(k)));
  return hit ? hit.reply : null;
};


const ChatWidget = ({ isLoggedIn, navigateTo }) => {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', text: "Hi! I'm Axigon. Ask me anything about money, taxes, career, or legal questions in India. No sign-up needed." },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [convId, setConvId] = useState(null);
  const [unread, setUnread] = useState(0);
  const fallbackIdx = useRef(0);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  // Pick a graceful reply when the live backend is unavailable: prefer a
  // keyword-matched common response, otherwise rotate through generic messages.
  const fallbackReply = (text) => {
    const canned = matchCommonResponse(text);
    if (canned) return canned;
    const msg = GENERIC_FALLBACKS[fallbackIdx.current % GENERIC_FALLBACKS.length];
    fallbackIdx.current += 1;
    return msg;
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setLoading(true);

    try {
      const res = await fetch('/api/finance?action=unified-chat', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, conversationId: convId }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 429) {
        // Rate limited — show the server's "slow down" message directly.
        setMessages(prev => [...prev, { role: 'assistant', text: data.error || "You're sending messages a bit fast. Please wait a moment." }]);
        if (!open) setUnread(n => n + 1);
        setLoading(false);
        return;
      }
      if (!res.ok) throw new Error(data.error || 'Request failed');
      if (data.conversationId) setConvId(data.conversationId);
      // A valid response uses the live reply; anything missing falls back gracefully.
      const reply = data.response || fallbackReply(text);
      setMessages(prev => [...prev, { role: 'assistant', text: reply }]);
      if (!open) setUnread(n => n + 1);
    } catch (err) {
      // Never surface a raw error to the user — degrade to a helpful message.
      setMessages(prev => [...prev, { role: 'assistant', text: fallbackReply(text) }]);
      if (!open) setUnread(n => n + 1);
    }
    setLoading(false);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      <style>{`
        @keyframes cw-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.5; transform: scale(0.85); }
        }
        @keyframes cw-ring {
          0%   { transform: scale(1);    opacity: 0.6; }
          100% { transform: scale(2.2);  opacity: 0; }
        }
        @keyframes cw-slidein {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes cw-bounce-dot {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(-3px); }
        }
        .cw-msg   { max-width: 82%; word-break: break-word; }
        .cw-input { outline: none; }
      `}</style>

      <div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 9000, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>

        {/* ── Chat panel (expanded) ── */}
        {open && (
          <div style={{
            width: '340px',
            backgroundColor: 'var(--bg)',
            border: '1px solid var(--border)',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.16)',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
            animation: 'cw-slidein 0.18s ease-out',
          }}>
            {/* Panel header */}
            <div style={{
              padding: '14px 16px',
              background: 'linear-gradient(135deg, #0a2818 0%, #0f3d22 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <div style={{ width: '34px', height: '34px', borderRadius: '50%', backgroundColor: 'rgba(0,194,124,0.2)', border: '1px solid rgba(0,194,124,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#00C27C' }}>Ax</span>
                  </div>
                  {/* Live dot on avatar */}
                  <span style={{ position: 'absolute', bottom: 0, right: 0, width: '9px', height: '9px', borderRadius: '50%', backgroundColor: '#00C27C', border: '2px solid #0a2818', display: 'block' }} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#ffffff', letterSpacing: '-0.01em' }}>Axigon AI</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '1px' }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#00C27C', display: 'inline-block', animation: 'cw-pulse 1.8s ease-in-out infinite' }} />
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>Live agent · typically replies in seconds</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', transition: 'color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.9)'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
                aria-label="Close chat"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* Messages */}
            <div style={{
              flex: 1, overflowY: 'auto', padding: '14px 14px 8px',
              display: 'flex', flexDirection: 'column', gap: '10px',
              maxHeight: '320px', minHeight: '180px',
            }}>
              <>
                  {messages.map((m, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                      <div className="cw-msg" style={{
                        padding: '9px 12px',
                        borderRadius: m.role === 'user' ? '12px 12px 3px 12px' : '12px 12px 12px 3px',
                        backgroundColor: m.role === 'user' ? 'var(--accent)' : 'var(--bg-secondary)',
                        color: m.role === 'user' ? 'white' : 'var(--text-primary)',
                        fontSize: '13px', lineHeight: 1.55,
                        border: m.role === 'user' ? 'none' : '1px solid var(--border)',
                      }}>{m.text}</div>
                    </div>
                  ))}
                  {loading && (
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <div style={{ padding: '9px 14px', borderRadius: '12px 12px 12px 3px', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', display: 'flex', gap: '4px', alignItems: 'center' }}>
                        {[0, 1, 2].map(d => (
                          <span key={d} style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: 'var(--text-tertiary)', display: 'inline-block', animation: `cw-bounce-dot 1s ease-in-out ${d * 0.18}s infinite` }} />
                        ))}
                      </div>
                    </div>
                  )}
                  <div ref={bottomRef} />
                </>
            </div>

            {/* Input */}
            <div>
              <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px', alignItems: 'flex-end', backgroundColor: 'var(--bg)' }}>
                <textarea
                  ref={inputRef}
                  className="cw-input"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder="Ask anything..."
                  rows={1}
                  style={{
                    flex: 1, resize: 'none', border: '1px solid var(--border)',
                    borderRadius: '8px', padding: '8px 10px',
                    fontSize: '13px', fontFamily: 'inherit', color: 'var(--text-primary)',
                    backgroundColor: 'var(--bg-secondary)', lineHeight: 1.5,
                    maxHeight: '80px', overflowY: 'auto',
                  }}
                  onInput={e => {
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 80) + 'px';
                  }}
                />
                <button
                  onClick={send}
                  disabled={!input.trim() || loading}
                  style={{
                    width: '34px', height: '34px', borderRadius: '8px', border: 'none',
                    backgroundColor: input.trim() && !loading ? 'var(--accent)' : 'var(--border)',
                    color: 'white', cursor: input.trim() && !loading ? 'pointer' : 'default',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    transition: 'background-color 0.2s',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M1 7h12M7 1l6 6-6 6" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              {!isLoggedIn && (
                <p style={{ margin: 0, padding: '0 12px 10px', fontSize: '11px', color: 'var(--text-tertiary)', textAlign: 'center', lineHeight: 1.5 }}>
                  <button
                    onClick={() => { setOpen(false); navigateTo('signup'); }}
                    style={{ background: 'none', border: 'none', padding: 0, color: 'var(--accent)', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', fontSize: '11px' }}
                  >Sign up</button> to get answers tailored to your own numbers.
                </p>
              )}
            </div>
          </div>
        )}

        {/* ── Launcher card (always visible unless dismissed or chat open) ── */}
        {!open && !dismissed && (
          <div style={{
            backgroundColor: 'var(--bg)',
            border: '1px solid var(--border)',
            borderRadius: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            padding: '14px 16px',
            display: 'flex', alignItems: 'center', gap: '12px',
            animation: 'cw-slidein 0.2s ease-out',
            minWidth: '260px',
          }}>
            {/* Avatar + live ring */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'linear-gradient(135deg, #0f3d22 0%, #00C27C 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: 'white' }}>Ax</span>
              </div>
              {/* Pulsing ring */}
              <span style={{
                position: 'absolute', inset: '-4px', borderRadius: '50%',
                border: '2px solid rgba(0,194,124,0.4)',
                animation: 'cw-ring 1.8s ease-out infinite',
                pointerEvents: 'none',
              }} />
              {/* Solid dot */}
              <span style={{
                position: 'absolute', bottom: '1px', right: '1px',
                width: '10px', height: '10px', borderRadius: '50%',
                backgroundColor: '#00C27C', border: '2px solid var(--bg)',
                animation: 'cw-pulse 2s ease-in-out infinite',
              }} />
            </div>

            {/* Text */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>Chat with Live Agent</span>
                {unread > 0 && (
                  <span style={{ backgroundColor: '#EF4444', color: 'white', fontSize: '10px', fontWeight: 700, borderRadius: '10px', padding: '1px 6px', minWidth: '18px', textAlign: 'center' }}>{unread}</span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#00C27C', display: 'inline-block', animation: 'cw-pulse 1.8s ease-in-out infinite', flexShrink: 0 }} />
                <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>We're online · Axigon AI</span>
              </div>
            </div>

            {/* Start chat button */}
            <button
              onClick={() => setOpen(true)}
              style={{
                backgroundColor: 'var(--accent)', color: 'white', border: 'none',
                borderRadius: '8px', padding: '8px 14px',
                fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0,
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--accent-hover)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'var(--accent)'}
            >Start chat</button>

            {/* Dismiss */}
            <button
              onClick={() => setDismissed(true)}
              aria-label="Dismiss"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: '2px', display: 'flex', flexShrink: 0, transition: 'color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--text-secondary)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        )}

        {/* ── Compact bubble (shown after dismiss or when chat is open) ── */}
        {(dismissed || open) && (
          <button
            onClick={() => { setOpen(o => !o); if (dismissed) setDismissed(false); }}
            aria-label={open ? 'Close chat' : 'Open chat'}
            style={{
              width: '52px', height: '52px', borderRadius: '50%',
              backgroundColor: 'var(--accent)', border: 'none',
              boxShadow: '0 4px 20px rgba(0,194,124,0.35)',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'transform 0.2s, box-shadow 0.2s', position: 'relative',
            }}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.08)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          >
            {open ? (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M2 2l14 14M16 2L2 16" stroke="white" strokeWidth="2.2" strokeLinecap="round"/>
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <path d="M11 2C6.03 2 2 5.69 2 10.2c0 2.42 1.13 4.6 2.96 6.1L4 20l3.96-1.64A9.7 9.7 0 0011 18.4c4.97 0 9-3.69 9-8.2S15.97 2 11 2z" fill="white"/>
              </svg>
            )}
            {!open && unread > 0 && (
              <span style={{ position: 'absolute', top: '-3px', right: '-3px', width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#EF4444', color: 'white', fontSize: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid white' }}>{unread}</span>
            )}
          </button>
        )}

      </div>
    </>
  );
};

export default ChatWidget;
